<template lang="pug">
    .registration-template.full-height-error.not-valid(v-if="!enabledExtensions.includes('event-manager') || !hasRegistrationTemplates")
        Error(:message="null" :code="403")
    .registration-template.full-height-error.not-allowed(v-else-if="pathNotValid")
        Error(:message="$t('URL Not Valid')")
    .registration-template.full-height-error(v-else-if="error")
        template(v-if="error.code")
            Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
        template(v-else)
            Error(:message="$t('Something went wrong!')" :code="501")
    .registration-template(v-else)
        tabs(:items="tabs" :active-tab="$route.fullPath" @click="activateTab")
            template(slot="tabbar-buttons")
                el-tooltip(v-if="! loading && isDeletable" class="item" effect="dark" :content="$t('Delete')" placement="top")
                    el-button.delete-button(
                        circle
                        type="danger"
                        icon="el-icon-fa-trash-alt far"
                        :loading="deleting"
                        :disabled="isDisabledSubmit && ! deleting"
                        @click="deleteCurrentRegistrationTemplate"
                    )
                el-tooltip(class="item" effect="dark" :content="$t('Dismiss')" placement="top")
                    el-button(
                        circle icon="el-icon-fa-times fas"
                        :disabled="isDisabledSubmit"
                        @click="leave"
                    )
                el-tooltip(v-if="! loading" class="item" effect="dark" :content="$t('Save and close')" placement="top")
                    el-button(
                        circle
                        type="primary"
                        icon="el-icon-fa-check fas"
                        :disabled="isDisabledSubmit && ! savingAndClose"
                        :loading="savingAndClose"
                        @click="submit($event, listRoute)"
                    )
                el-tooltip(v-if="! loading" class="item" effect="dark" :content="$t('Save')" placement="top")
                    el-button(
                        circle
                        type="success"
                        icon="el-icon-fa-save fas"
                        :disabled="isDisabledSubmit && ! saving"
                        :loading="saving"
                        @click="submit"
                    )

        el-form(label-position="top" :model="form" :rules="rules" ref="form")
            all-input-fields
            el-card(shadow="always" v-loading="loading")
                router-view.registration-template-form(:key="$route.path" :class="{'loading-panel': loading}")
            .form-buttons
                el-button(
                    round v-if="! loading"
                    icon="el-icon-fa-times fas"
                    :disabled="isDisabledSubmit"
                    @click="leave"
                ) {{ $t('Dismiss') }}
                el-button(
                    round v-if="! loading"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="savingAndClose"
                    :disabled="isDisabledSubmit && ! savingAndClose"
                    @click="submit($event, listRoute)"
                ) {{ $t('Save and close') }}
                el-button(
                    round v-if="! loading"
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="isDisabledSubmit && ! saving"
                    @click="submit"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "en": {
        "Events still use this form: {count}. Deleting it switches them back to the fixed fields. Continue?": "Events still use this form: {count}. Deleting it switches them back to the fixed fields. Continue?"
    },
    "de": {
        "Registration form could not be fetched.": "Anmeldeformular konnte nicht gefunden werden.",
        "Registration form could not be deleted.": "Anmeldeformular konnte nicht gelöscht werden.",
        "Registration form deleted successfully!": "Anmeldeformular erfolgreich gelöscht!",
        "Registration form could not be saved.": "Anmeldeformular konnte nicht gespeichert werden.",
        "Registration form saved successfully!": "Anmeldeformular erfolgreich gespeichert!",
        "Registration form Not Found": "Anmeldeformular nicht gefunden",
        "Registration form": "Anmeldeformular",
        "Every field needs a label.": "Jedes Feld braucht eine Bezeichnung.",
        "Every choice field needs at least one option.": "Jedes Auswahlfeld braucht mindestens eine Option.",
        "Events still use this form: {count}. Deleting it switches them back to the fixed fields. Continue?": "Dieses Formular wird noch von Events verwendet: {count}. Beim Löschen fallen sie auf die Pflichtfelder zurück. Fortfahren?",
        "Delete registration form": "Anmeldeformular löschen",
        "Delete": "Löschen",
        "Cancel": "Abbrechen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore, useEventManagerRegistrationTemplateStore } from '@/stores';
import { FIELD_TYPES_WITH_OPTIONS, isFixedField } from '@/stores/eventManagerRegistrationTemplate';

import router from '@/router';

import Tabs from '@/components/Tabs';
import Error from '@/components/Error';

// validate all fields, regardless if the corresponding component is mounted or not
import AllInputFields from './AllInputFields';

export default {
    name: 'RegistrationTemplate',
    data() {
        return {
            listRoute: { name: 'event-registration-template-list' },
            error: false,
            pathNotValid: false,
            loading: false,
            saving: false,
            savingAndClose: false,
            deleting: false
        };
    },
    components: {
        Tabs,
        Error,
        AllInputFields
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) {
                this.registrationTemplateStore.setFormInitialized(false);
                this.initialize();
            }
        }
    },
    computed: {
        id() {
            return _.get(this.form, 'id', false);
        },
        isDeletable() {
            return !!this.id;
        },
        isDisabledSubmit() {
            return this.saving || this.savingAndClose || this.deleting;
        },
        tabs() {
            const id = this.$route.params.id;
            const name = _.get(this.form, 'name') || this.$t('Registration form');

            return [
                {
                    name,
                    path: `/event-manager/registration-template/${id}/main`,
                    icon: {
                        name: 'clipboard-list',
                        group: 'fas'
                    }
                }
            ];
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        // registration forms are a storefront feature (citystore-ssr)
        hasRegistrationTemplates() {
            return useSetupStore().enabledFeatures.includes('event-manager:registration-templates');
        },
        setupStore() {
            return useSetupStore();
        },
        registrationTemplateStore() {
            return useEventManagerRegistrationTemplateStore();
        },
        form() {
            return this.registrationTemplateStore.form;
        },
        initialized() {
            return this.registrationTemplateStore.formInitialized;
        },
        validateOnEnter() {
            return this.registrationTemplateStore.formValidateOnEnter;
        },
        rules() {
            return this.registrationTemplateStore.rules;
        }
    },
    methods: {
        initialize() {
            if (!this.enabledExtensions.includes('event-manager') || !this.hasRegistrationTemplates) {
                this.registrationTemplateStore.setFormInitialized(true);
                return;
            }

            if (this.initialized !== true) {
                this.registrationTemplateStore.setFormInitialized(true);

                this.error = false;
                this.loading = true;

                if (this.$route.params.id === 'new') {
                    this.registrationTemplateStore.reset();
                    this.registrationTemplateStore.resetRules();
                    this.loading = false;
                } else if (!isNaN(this.$route.params.id)) {
                    this.registrationTemplateStore.fetchRegistrationTemplate(this.$route.params.id)
                        .then(() => {
                            this.loading = false;
                            this.registrationTemplateStore.resetRules();
                        })
                        .catch(err => {
                            const { graphQLErrors } = err;

                            let code = 501;
                            let message = 'An error occurred!';

                            if (graphQLErrors) {
                                code = _.get(graphQLErrors, '0.code') || code;
                                message = _.get(graphQLErrors, '0.message') || message;
                            } else {
                                this.error = true;
                            }

                            switch (code) {
                                case 401:
                                    this.error = { code, message: 'You are not authorized' };
                                    break;
                                case 403:
                                    this.error = { code, message: 'You are not allowed to be here' };
                                    break;
                                case 404:
                                    this.error = { code, message: 'Registration form Not Found' };
                                    break;
                                default:
                                    this.$notify.error({
                                        title: this.$t('An error occurred!'),
                                        message: this.$t('Registration form could not be fetched.'),
                                        iconClass: 'fas el-icon-fa-times',
                                        position: 'bottom-left',
                                        customClass: 'dark',
                                        duration: 8000
                                    });
                                    break;
                            }

                            this.loading = false;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);
                        });
                } else {
                    this.loading = true;
                    this.pathNotValid = true;
                }
            } else {
                if (this.validateOnEnter === true) {
                    setTimeout(() => {
                        this.$refs.form.validate(() => {
                            // callback function is required :(
                        });
                    }, 100);
                }
            }
        },
        deleteCurrentRegistrationTemplate() {
            const eventsCount = _.get(this.form, 'events_count') || 0;

            const confirmation = eventsCount
                ? this.$confirm(
                    this.$t('Events still use this form: {count}. Deleting it switches them back to the fixed fields. Continue?', { count: eventsCount }),
                    this.$t('Delete registration form'),
                    { confirmButtonText: this.$t('Delete'), cancelButtonText: this.$t('Cancel'), type: 'warning' }
                )
                : Promise.resolve();

            confirmation.then(() => {
                this.deleting = true;
                this.registrationTemplateStore.deleteRegistrationTemplate(this.id)
                    .then(() => {
                        router.push(this.listRoute);

                        this.$notify.success({
                            title: this.$t('Registration form deleted successfully!'),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    })
                    .catch(err => {
                        /* eslint no-console: ["error", { allow: ["error"] }] */
                        console.error(err);

                        this.deleting = false;

                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Registration form could not be deleted.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    });
            }).catch(() => {
                // dismissed
            });
        },
        // The field list is dynamic, so its checks live here rather than in the rule object.
        validateFields() {
            const fields = _.get(this.form, 'fields') || [];

            if (fields.some(field => !_.trim(field.label))) {
                return 'Every field needs a label.';
            }

            // The fixed selects (salutation, slot) get their choices from the booker / the event.
            const choiceWithoutOptions = fields.some(field =>
                !isFixedField(field)
                && FIELD_TYPES_WITH_OPTIONS.includes(field.type)
                && !(field.options || []).some(option => _.trim(option.label))
            );
            if (choiceWithoutOptions) {
                return 'Every choice field needs at least one option.';
            }

            return null;
        },
        submit(event, redirect = false) {
            this.$refs.form.validate(valid => {
                const fieldError = this.validateFields();

                if (valid && fieldError) {
                    this.$notify.error({
                        title: this.$t('Registration form could not be saved.'),
                        message: this.$t(fieldError),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    return;
                }

                if (valid) {
                    if (redirect) this.savingAndClose = true;
                    else this.saving = true;

                    let save = template => this.registrationTemplateStore.create(template);
                    if (this.id) save = template => this.registrationTemplateStore.update(template);

                    save(this.form)
                        .then(template => {
                            this.$notify.success({
                                title: this.$t('Registration form saved successfully!'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });

                            if (redirect) {
                                router.push(redirect);
                            } else {
                                if (this.$route.params.id === 'new') {
                                    const route = router.currentRoute;
                                    route.params.id = template.id;
                                    router.replace(route);
                                }
                                this.saving = false;
                            }
                        })
                        .catch(err => {
                            const { graphQLErrors } = err;

                            let message = 'Registration form could not be saved.';
                            if (graphQLErrors) {
                                message = _.get(graphQLErrors, '0.message') || message;
                            }

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t(message),
                                iconClass: 'fas el-icon-fa-times',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });

                            this.saving = false;
                            this.savingAndClose = false;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);
                        });
                } else {
                    this.registrationTemplateStore.setFormValidateOnEnter(true);

                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            });
        },
        leave() {
            router.push(this.listRoute);
        },
        activateTab(item) {
            return this.setupStore.activateTab(item);
        }
    },
    mounted() {
        this.initialize();
    },
    beforeRouteEnter(to, from, next) {
        // redirect to the main tab if necessary
        const id = _.get(to, 'params.id') || 'new';

        if (to.name === 'event-registration-template-form') {
            next({ name: 'event-registration-template-main', params: { id } });
        } else {
            next();
        }
    },
    beforeRouteLeave(to, from, next) {
        next();

        const registrationTemplateStore = useEventManagerRegistrationTemplateStore();
        setTimeout(() => {
            registrationTemplateStore.reset();
            registrationTemplateStore.resetUI();
            this.$nextTick(() => registrationTemplateStore.resetRules());
        }, 300);
    }
};
</script>

<style lang="sass" scoped>
.registration-template::v-deep
    .el-card__body
        padding: 0
        & > section
            padding: 20px
</style>
